import { useState, useCallback } from 'react';

// Row selection for the bill validation tables. Lifted out of BillsListPage so
// the Site Invoices tab (job 81) and the Bills tab (job 82) share one behaviour
// rather than three near-copies.

export function nextSelection(current: Set<string>, id: string): Set<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

export function allSelected(selected: Set<string>, ids: string[]): boolean {
  return ids.length > 0 && ids.every((id) => selected.has(id));
}

export function someSelected(selected: Set<string>, ids: string[]): boolean {
  return ids.some((id) => selected.has(id));
}

/**
 * What the header checkbox does to a page of ids. A page that is entirely
 * selected clears — but only its own ids, leaving other pages' selections
 * alone. A page that is partly or wholly unselected fills up instead of
 * clearing, because clearing would discard work the user has already done.
 */
export function nextSelectionAll(current: Set<string>, ids: string[]): Set<string> {
  if (allSelected(current, ids)) {
    const next = new Set(current);
    ids.forEach((id) => next.delete(id));
    return next;
  }
  return new Set([...current, ...ids]);
}

export function useRowSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelected((cur) => nextSelection(cur, id));
  }, []);

  const toggleAll = useCallback((ids: string[]) => {
    setSelected((cur) => nextSelectionAll(cur, ids));
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selected,
    count: selected.size,
    isSelected: (id: string) => selected.has(id),
    toggle,
    toggleAll,
    clear,
    allSelected: (ids: string[]) => allSelected(selected, ids),
    someSelected: (ids: string[]) => someSelected(selected, ids),
  };
}
